import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import Tooltip from 'components/application/Tooltip';

import PersonnelCourseTrainingStatusIndicator from 'components/personnel/PersonnelCourseTrainingStatusIndicator';

import { collectData } from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function SelectedMembersList({
  domainPersonnelCollection,
  isFetching = false,
  onBookingGroupMemberDelete,
  selectedMemberIds,
  shouldBlockFullRemoval = false,
}) {
  const queryClient = useQueryClient();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.SelectedMembersList',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const collectedAssignableMembers = collectData({
    queryClient,
    queryKey: ['assignableBookees'],
  });

  const currentBookingGroupMembers = selectedMemberIds
    .map((personnelId) => {
      const persistedPersonnel = domainPersonnelCollection?.find(
        (personnel) => personnel.id === personnelId,
      );
      const selectedMember = (
        persistedPersonnel ?
          domainPersonnelCollection
        : collectedAssignableMembers).find(
        (assignableMember) => assignableMember.id === personnelId,
      );
      if (!selectedMember) return null;

      return {
        id: personnelId,
        externalId: selectedMember.attributes.externalId,
        trainingStatus: selectedMember.attributes.trainingStatus,
        name: personDisplayName({ attributes: selectedMember.attributes }),
      };
    })
    .filter(Boolean);

  const isBlockingLastMemberRemoval =
    shouldBlockFullRemoval && currentBookingGroupMembers.length === 1;

  return (
    <div className='form-container'>
      {isFetching && (
        <div
          className='tw-group/pill chip-item-pill w-100 tw-relative tw-mb-2 tw-flex tw-h-10 tw-items-center tw-justify-between tw-rounded-md tw-bg-grey-050 tw-px-3 tw-py-2'
          key='loading-pill'
        >
          <div className='tw-flex tw-items-center tw-py-2'>
            <PersonnelCourseTrainingStatusIndicator status={5} />
            <div className='tw-ml-2 tw-mr-2 tw-text-s tw-font-medium tw-text-grey-900'>
              {i18nTCommon('shared.loading...')}
            </div>
          </div>
        </div>
      )}
      {currentBookingGroupMembers?.map((member) => (
        <div
          className='tw-group/pill chip-item-pill w-100 tw-relative tw-mb-2 tw-flex tw-h-10 tw-items-center tw-justify-between tw-rounded-md tw-bg-grey-050 tw-px-3 tw-py-2'
          key={member.id}
        >
          <div className='tw-flex tw-items-center tw-py-2'>
            <PersonnelCourseTrainingStatusIndicator
              status={member.trainingStatus}
            />
            <div className='tw-ml-2 tw-mr-2 tw-text-s tw-font-medium tw-text-grey-900'>
              {member.name}
            </div>
            {member.externalId && (
              <div className='tw-text-s tw-font-normal tw-text-grey-500'>
                {member.externalId}
              </div>
            )}
          </div>
          {!(
            shouldBlockFullRemoval && domainPersonnelCollection?.length === 1
          ) && (
            <ConditionalWrapper
              condition={isBlockingLastMemberRemoval}
              wrapper={(children) => (
                <Tooltip
                  placement='top'
                  tooltip={i18nT('disabledTooltip')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <div
                className={classNames(
                  "circle--remove tw-ml-2 before:tw-content-[''] after:tw-content-['']",
                  isBlockingLastMemberRemoval ?
                    'before:tw-bg-grey-300 after:tw-bg-grey-300 hover:before:tw-bg-grey-300 hover:after:tw-bg-grey-300'
                  : 'before:tw-bg-grey-900 after:tw-bg-grey-900 hover:before:tw-bg-white hover:after:tw-bg-white',
                )}
                data-element-name='member-removal-icon'
                onClick={() => {
                  !isBlockingLastMemberRemoval &&
                    onBookingGroupMemberDelete(member.id);
                }}
              />
            </ConditionalWrapper>
          )}
        </div>
      ))}
    </div>
  );
}

SelectedMembersList.propTypes = {
  selectedMemberIds: PropTypes.array.isRequired,
  domainPersonnelCollection: PropTypes.arrayOf(resourceShape('personnel')),
  onBookingGroupMemberDelete: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  shouldBlockFullRemoval: PropTypes.bool,
};
